import ChatCard from '../ChatCard';
import { Button } from '@/shared/ui/button.tsx';
import { Calendar } from '@/shared/ui/calendar.tsx';
import { SetStateAction } from 'react';
import { useMemo } from "react"
import { addMonths, isAfter, parseISO } from "date-fns"

type Props = {
  date: Date | undefined,
  setDate: React.Dispatch<SetStateAction<Date | undefined>>,
  setStep: (id: "first" | "date" | "result" | "reasoning") => void,
  maxEndDate: string,
}

const DateSelection = ({ setStep, setDate, date, maxEndDate }: Props) => {

  const maxSelectableDate = useMemo(() => {
    const parsedDate = parseISO(maxEndDate)
    const twoMonthsFromNow = addMonths(new Date(), 2)
    return isAfter(parsedDate, twoMonthsFromNow) ? twoMonthsFromNow : parsedDate
  }, [maxEndDate])

  return (
    <>
      <ChatCard user="client">
        <div className="flex w-full items-center justify-between">
          <Button
            onClick={() => setStep("first")}
            variant="outline"
            className="rounded-full font-poppins border-2 border-purple-400 hover:bg-white tracking-wide text-purple-600"
          >Back to themes</Button>
          <p className="w-full text-end font-poppins">I'd like to select a date</p>
        </div>
      </ChatCard>
      <ChatCard user="assistant" key="date">
        <div className="w-full flex flex-col gap-4">
          <p className="tracking-wider font-poppins text-sm text-gray-500">Pick a date from below to view themes for that day</p>
          <div className="w-full">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(date: Date | undefined) => {
                if (date) {
                  setDate(date);
                  setStep("first")
                } else {
                  setDate(new Date())
                  setStep("first")
                }
              }}
              disabled={{
                after: maxSelectableDate,
                before: new Date()
              }}
              className="w-full"
              classNames={{
                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 w-full",
                month: "space-y-4 w-full",
                table: "w-full border-collapse space-y-1",
                head_row: "flex w-full",
                head_cell: "rounded-md w-full font-normal text-[0.8rem] text-center py-2",
                row: "flex w-full mt-2",
                cell: "flex-1 text-center text-sm p-1 relative rounded-lg focus-within:relative focus-within:z-20",
                day: "h-9 w-full p-0 font-normal aria-selected:opacity-100 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center",
                day_selected:
                  "flex-1 rounded-full font-poppins border-2 border-purple-400 hover:bg-white tracking-wide text-purple-600",
                day_today: "bg-blue-50 font-semibold text-blue-900",
                day_outside: "text-muted-foreground opacity-50",
                day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
              }}
            />
            <div className="w-full flex justify-end">
            </div>
          </div>
        </div>
      </ChatCard>
    </>
  )
}

export default DateSelection
